import { Localize } from "../utils/Localize";

import { AbstractTextContentVO } from "./AbstractTextContentVO";
import { DateTimeStyle } from "../globalization/DateTimeStyle";

export class LocalizedDateTimeVO extends AbstractTextContentVO {
    public static NAME: string = "LocalizedDateTimeVO";

    private _date: Date;
    private _pattern: string;
    private _dateStyle: string;
    private _timeStyle: string;

    constructor(date: Date = null, dateStyle: string = null, timeStyle: string = null) {
        super();
        this._date = date || new Date();
        this._dateStyle = dateStyle || DateTimeStyle.SHORT;
        this._timeStyle = timeStyle || this._dateStyle;
    }

    /**
     * A Date instance to be used
     */
    public get date(): Date {
        return this._date;
    }

    public set date(date: Date) {
        this._date = date;
        this.propertyChangedSignal.dispatch();
    }

    /**
     * String key for a date pattern.
     *
     * <p>The<code>datePattern</code> key will be used to configure format of the date manually.
     * If you define your own pattern, dateStyle variable will have no impact on the date.</p>
     *
     * <p>You can find more information about patterns here: 
     * http://help.adobe.com/en_US/FlashPlatform/reference/actionscript/3/flash/globalization/DateTimeFormatter.html</p>
     *
     * <p>Example for datePattern key: "d. MMMM yyyy", where are: d - day of the month(numeric),
     *  MMMM - month of the year, yyyy - year. If you use some symbols between digits,
     * they will be shown too. The final version of the localized date in this case: 
     * "&lt;localized day&gt;&lt;point&gt; &lt;localized month&gt; &lt;localized year&gt;". E.g.: 12. Mai 2013 (german)</p>
     *
     // tslint:disable-next-line:max-line-length
     * @see http://help.adobe.com/en_US/FlashPlatform/reference/actionscript/3/flash/globalization/DateTimeFormatter.html#setDateTimePattern()
     */
    public get pattern(): string {
        return this._pattern;
    }

    public set pattern(pattern: string) {
        this._pattern = pattern;
        this.propertyChangedSignal.dispatch();
    }

    /**
     * String key for a date style.
     *
     * <p>The<code>dateStyle</code> key will be used to set the style of the localized date.
     * Constants, which you can use as style, are defined in the class "DateTimeStyle.ts".</p>
     *
     * <p>Examples for the style: "DateTimeStyle.NONE", "DateTimeStyle.LONG", "DateTimeStyle.SHORT".</p>
     */
    public get dateStyle(): string {
        return this._dateStyle;
    }

    public set dateStyle(style: string) {
        this._dateStyle = style;
        this.propertyChangedSignal.dispatch();
    }

    /**
     * String key for a time style.
     *
     * <p>The<code>timeStyle</code> key will be used to set the style of the localized time.
     * Constants, which you can use as style, are defined in the class "DateTimeStyle.ts".</p>
     *
     * <p>Examples for the style: "DateTimeStyle.NONE", "DateTimeStyle.LONG", "DateTimeStyle.SHORT".</p>
     */
    public get timeStyle(): string {
        return this._timeStyle;
    }

    public set timeStyle(timeStyle: string) {
        this._timeStyle = timeStyle;
    }

    public compose(): string {
        return Localize.datetime(this._date, this._dateStyle, this._timeStyle);
    }

    public toString(): string {
        // tslint:disable-next-line:max-line-length
        return (
            LocalizedDateTimeVO.NAME +
            ", current date: " +
            this._date +
            ", date style: " +
            this._dateStyle +
            ", date pattern: " +
            this._pattern
        );
    }
}
